import React from 'react';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import {
    BudgetProposalResourceMetadataT,
    CellRendererParams,
    InscriptionWithMetadata,
    OperationT,
    OperationType,
    TypedOperationT,
} from 'routes/BudgetModule/config/types';

import {
    BUDGET_NAME_FIELD,
    DRAFT_OPERATION_SOFTWARE_ID,
    GESTIONNAIRE_SOFTWARE_ID,
    INSCRIPTION_EXERCICE,
    INSCRIPTION_EXPENSE_INCOME,
    INSCRIPTION_ID,
    OPERATION_NAME_FIELD,
    OPERATION_SOFTWARE_ID,
    ORG_CHART_FINANCE_SOFTWARE_ID,
    SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { useUpdateOrphanInscription } from '../helpers/hooks/updateInscription';
import { getOperationNameFromSoftwareId } from '../helpers/dataHelpers';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useTranslation } from 'react-i18next';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    lineHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    '& .MuiInputBase-root': {
        paddingBottom: 0,
        paddingTop: 0,
        width: '100%',
    },
});

const StySelect = styled(Select, {
    name: 'StySelect',
})(({ theme }) => ({
    height: '33px',
    padding: 0,
    '& .MuiInputBase-root.Mui-disabled': {
        backgroundColor: theme.palette.lightGrey,
    },
    '& .MuiInputBase-input': {
        height: 'inherit',
        lineHeight: '16px',
    },
    '& .MuiMenuItem-root': {
        fontSize: '12px',
        paddingLeft: 0,
    },
}));

type OperationCellRendererParams = CellRendererParams<
    InscriptionWithMetadata,
    OperationT['operation.softwareId']
> & {
    trackingInformation?: {
        eventName: string;
        metadata?: BudgetProposalResourceMetadataT;
    };
    queryCacheKey: InscriptionQueryKey;
    operations?: TypedOperationT[];
};

export default function OperationCellRenderer({
    data,
    // value here is the Operation software_id
    value,
    trackingInformation,
    queryCacheKey,
    setValue,
    operations,
}: OperationCellRendererParams): React.ReactElement {
    const { t } = useTranslation();
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const updateInscriptionOperationMutation = useUpdateOrphanInscription(
        queryCacheKey,
        trackingInformation,
    );
    if (value === null || data === undefined) return <span />;

    const { inscription } = data;
    const chooseValueLabel = t(
        'budgetProposal.chooseASecondaryBudgetAxisElement',
        {
            context: sourceTable,
        },
    );
    return (
        <StyBox>
            <StySelect
                value={value}
                displayEmpty
                variant="outlined"
                inputProps={{ disabled: !inscription.canEdit }}
                renderValue={(selectedValue: string) => {
                    const operationName = getOperationNameFromSoftwareId(
                        selectedValue,
                        // TODO StrictNullCheck : Try to remove "!"
                        operations!,
                        chooseValueLabel,
                    );
                    return (
                        <Tooltip title={operationName}>
                            <MenuItem value={selectedValue}>
                                {operationName}
                            </MenuItem>
                        </Tooltip>
                    );
                }}
                onChange={(event) => {
                    const softwareId = event.target.value as string;

                    const operation = operations?.find(
                        (op) =>
                            op['operation.softwareId'] === event.target.value,
                    );

                    const dataKey =
                        operation?.type === OperationType.DRAFT
                            ? DRAFT_OPERATION_SOFTWARE_ID
                            : SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID;
                    updateInscriptionOperationMutation.mutate({
                        id: inscription[INSCRIPTION_ID],
                        data: {
                            [dataKey]: softwareId,
                        },
                    });

                    setValue?.(softwareId);

                    if (operation?.type === OperationType.DRAFT)
                        trackEventBudget(
                            TRACKABLE_EVENTS.budget.budgetOverview.operations
                                .draftOperation.associate,
                            {
                                budget: inscription[BUDGET_NAME_FIELD],
                                exercice: inscription[INSCRIPTION_EXERCICE],
                                section:
                                    inscription[INSCRIPTION_EXPENSE_INCOME],
                                resource:
                                    inscription[GESTIONNAIRE_SOFTWARE_ID] ||
                                    inscription[ORG_CHART_FINANCE_SOFTWARE_ID],
                            },
                        );
                }}
            >
                {operations?.map((operation) => {
                    const operationName = operation[OPERATION_NAME_FIELD];
                    return (
                        <MenuItem
                            key={operation[OPERATION_SOFTWARE_ID]}
                            value={operation[OPERATION_SOFTWARE_ID]}
                        >
                            {operationName !== ''
                                ? operationName
                                : chooseValueLabel}
                        </MenuItem>
                    );
                })}
            </StySelect>
        </StyBox>
    );
}
